import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, X } from 'lucide-react';
import { Input } from './ui/input';

/**
 * CityAutocomplete - Composant d'autocomplétion de villes françaises
 * Utilise l'API geo.api.gouv.fr (gratuite, sans clé API)
 */
export default function CityAutocomplete({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Entrez votre ville",
  className = "",
  required = false,
  "data-testid": dataTestId = "city-autocomplete"
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync with external value
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch cities from API
  const fetchCities = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // API geo.api.gouv.fr - communes françaises
      const response = await fetch(
        `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(query)}&fields=nom,code,codesPostaux,departement,region,centre&boost=population&limit=8`
      );
      
      if (response.ok) {
        const data = await response.json();
        const formattedSuggestions = data.map(city => ({
          name: city.nom,
          postalCode: city.codesPostaux?.[0] || '',
          department: city.departement?.nom || '',
          departmentCode: city.departement?.code || '',
          region: city.region?.nom || '',
          coordinates: city.centre?.coordinates || null, // [longitude, latitude]
          fullName: `${city.nom}${city.departement ? ` (${city.departement.code})` : ''}`
        }));
        setSuggestions(formattedSuggestions);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);
    
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API call
    debounceRef.current = setTimeout(() => {
      fetchCities(newValue);
      setShowSuggestions(true);
    }, 300);

    // Notify parent of raw input change
    if (onChange) {
      onChange(newValue);
    }
  };

  // Handle suggestion selection
  const handleSelect = (suggestion) => {
    setInputValue(suggestion.fullName);
    setSuggestions([]);
    setShowSuggestions(false);
    
    // Notify parent with full location data
    if (onSelect) {
      onSelect({
        location: suggestion.fullName,
        city: suggestion.name,
        postalCode: suggestion.postalCode,
        department: suggestion.department,
        departmentCode: suggestion.departmentCode,
        region: suggestion.region,
        coordinates: suggestion.coordinates // [longitude, latitude]
      });
    }
    
    if (onChange) {
      onChange(suggestion.fullName);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Clear input
  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (onChange) onChange('');
    if (onSelect) onSelect(null);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="input-gold pl-10 pr-10"
          required={required}
          autoComplete="off"
          data-testid={dataTestId}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold animate-spin" />
        )}
        {!isLoading && inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-dark-paper border border-white/20 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.name}-${suggestion.postalCode}`}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                index === highlightedIndex 
                  ? 'bg-gold/20 text-gold' 
                  : 'hover:bg-white/5 text-gray-300'
              }`}
            >
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gold" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{suggestion.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {suggestion.postalCode && `${suggestion.postalCode} - `}
                  {suggestion.department}
                  {suggestion.region && ` (${suggestion.region})`}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && inputValue.length >= 2 && !isLoading && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-dark-paper border border-white/20 rounded-md shadow-lg p-4 text-center text-gray-500">
          <p className="text-sm">Aucune ville trouvée pour "{inputValue}"</p>
          <p className="text-xs mt-1">Essayez avec un autre nom de ville</p>
        </div>
      )}
    </div>
  );
}
